import { BuilderService } from 'src/app/builder-services/builder.service'
import { Component, HostListener, OnInit } from '@angular/core'
import { TranslationService } from 'src/app/translations/translation.service'
import { FormBuilder, FormGroup } from '@angular/forms'
import { AddonsAccessService } from 'src/app/addons/addons-subscribtion/addons-access.service'
import { MatDialog } from '@angular/material/dialog'
import { LimitModalComponent } from 'src/app/free-plan/limit-modal/limit-modal.component'
import { Router } from '@angular/router'
import { Location } from '@angular/common'
import { environment } from 'src/environments/environment'

/**
 * Component: MultilingualComponent
 *
 * **Description:**
 * The `MultilingualComponent` manages the multilingual functionality of an application,
 * allowing users to handle a list of languages and select a default language. It provides
 * features to load existing languages from a database, add or delete languages, and synchronize
 * the selected languages with the UI.
 *
 * **Key Features:**
 * 1. **Language Management:**
 *    - Load languages from the database.
 *    - Automatically add "English" as the default language if no languages are present.
 *    - Add new languages dynamically via a dropdown.
 *    - Remove languages from the list while ensuring at least one language remains.
 *
 * 2. **Default Language Selection:**
 *    - Allows the user to select a default language from the available options.
 *    - Saves the default language to the database.
 *
 * 3. **Form Handling:**
 *    - Utilizes Angular's `FormBuilder` to manage the form for multilingual data.
 *    - Dynamically updates the list of languages in the table and dropdown.
 *
 * 4. **UI Integration:**
 *    - Includes a table to display the list of languages.
 *    - Uses `mat-select` for selecting multiple languages with dynamic updates.
 *    - Progress bar to indicate ongoing database operations.
 *
 * **Key Properties:**
 * - `defaultLanguage`: Stores the current default language.
 * - `language`: Array of objects representing the list of languages with their titles.
 * - `languagesToDelete`: Tracks languages marked for deletion.
 * - `multilingual`: Angular `FormGroup` for managing the multilingual form.
 * - `selectedLanguages`: Array of currently selected languages in the dropdown.
 * - `productProgress`: Boolean to indicate the progress of database operations.
 * - `languageCodes`: Array of predefined language codes for selection.
 *
 * **Key Methods:**
 * - `ngOnInit`: Initializes the component by loading languages and setting up the form.
 * - `initForm`: Initializes the multilingual form group.
 * - `loadLanguages`: Loads languages from the database and synchronizes them with the UI.
 * - `onLanguagesSelected`: Handles the selection of languages from the dropdown.
 * - `deleteRow`: Deletes a language from the list after user confirmation.
 * - `onSubmit`: Saves the current list of languages and the default language to the database.
 * - `selectDefaultLanguage`: Sets a selected language as the default.
 * - `refreshTable`: Refreshes the UI table to reflect changes in the language list.
 * - `getLanguageName`: Converts language keys to user-friendly display names.
 * - `getLanguageCodeKeys`: Retrieves the array of predefined language codes.
 *
 * **Dependencies:**
 * - Services:
 *    - `BuilderService`: Manages project-specific data.
 *    - `TranslationService`: Handles loading and saving language data.
 * - Angular Material Modules:
 *    - `MatFormFieldModule`, `MatSelectModule`, `MatOptionModule`, `MatTableModule`, `MatProgressBarModule`.
 * - Angular Core Modules:
 *    - `CommonModule`, `FormsModule`, `ReactiveFormsModule`.
 * - Third-Party Library:
 *    - `TranslateModule`: Provides translation capabilities for the UI.
 *
 * **Purpose:**
 * This component simplifies the management of multilingual functionality, enabling seamless
 * integration of multiple languages in the application and providing an intuitive user interface
 * for language selection and default language management.
 */

@Component({
  selector: 'app-multilingual',
  templateUrl: './multilingual.component.html',
  styleUrls: ['./multilingual.component.scss']
})
export class MultilingualComponent implements OnInit {
  addonName = 'Multilingual'
  showGracePeriodWarning = false
  gracePeriodInfo: any = null

  defaultLanguage: string = ''
  language: { title: string; isNew?: boolean }[] = []
  languagesToDelete: string[] = []
  multilingual!: FormGroup
  selectedLanguages: string[] = []
  productProgress: boolean = false
  project_id: string | undefined
  translateNumbers: boolean = true
  dropdownOpen = false
  dropdownSearch = ''
  currentPage = 1
  itemsPerPage = 4

  get totalPages(): number {
    return Math.ceil(this.language.length / this.itemsPerPage)
  }

  get pagedLanguages(): { title: string; isNew?: boolean }[] {
    const sorted = [...this.language].sort((a, b) => {
      if (a.title === this.defaultLanguage) return -1
      if (b.title === this.defaultLanguage) return 1
      return 0
    })
    const start = (this.currentPage - 1) * this.itemsPerPage
    return sorted.slice(start, start + this.itemsPerPage)
  }

  languageCodes: string[] = [
    'Arabic',
    'Armenian',
    'Bengali',
    'Bulgarian',
    'Burmese',
    'Chichewa',
    'Croatian',
    'Czech',
    'Danish',
    'Dutch',
    'English',
    'Estonian',
    'Fijian',
    'Filipino',
    'Finnish',
    'French',
    'Frisian',
    'Gaelic',
    'Georgian',
    'German',
    'Hebrew',
    'Hindi',
    'Hungarian',
    'Indonesian',
    'Inuktitut',
    'Italian',
    'Japanese',
    'Kannada',
    'Khmer',
    'Korean',
    'Kyrgyz',
    'Lao',
    'Latvian',
    'Lithuanian',
    'Malagasy',
    'Malay',
    'Malayalam',
    'Maltese',
    'Marathi',
    'Nepali',
    'Norwegian',
    'Pashto',
    'Persian',
    'Portuguese',
    'Punjabi',
    'Quichua',
    'Romanian',
    'Russian',
    'Samoan',
    'Serbian',
    'Serbo-croatian',
    'Sindhi',
    'Sinhala',
    'Slovak',
    'Slovenian',
    'Somali',
    'Sotho',
    'Spanish',
    'Sudanese',
    'Swahili',
    'Swedish',
    'Tagalog',
    'Tamil',
    'Telugu',
    'Thaana',
    'Thai',
    'Tigrinya',
    'Tongan',
    'Turkish',
    'Ukrainian',
    'Urdu',
    'Uyghur',
    'Uzbek',
    'Vietnamese',
    'Welsh',
    'Yiddish',
    'Zulu'
  ]

  constructor(private location: Location, private builderService: BuilderService, private translationService: TranslationService, private fb: FormBuilder, private addonsAccess: AddonsAccessService, public router: Router, private dialog: MatDialog) {
    this.project_id = this.builderService.selectedProject
  }

  ngOnInit(): void {
    this.addonsAccess.loadSubscriptions()
    this.addonsAccess.getSubscriptions$().subscribe(() => {
      this.checkGracePeriod()
    })
    this.loadLanguages()
    this.initForm()
  }
  prevPage(): void {
    if (this.currentPage > 1) this.currentPage--
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) this.currentPage++
  }

  navigateBack(): void {
    this.location.back();
  }
  checkGracePeriod() {
    if (this.addonsAccess.isAddonInGracePeriod(this.addonName)) {
      this.gracePeriodInfo = this.addonsAccess.getGracePeriodInfo(this.addonName)
      this.showGracePeriodWarning = true
    } else {
      this.showGracePeriodWarning = false
    }
  }

  isMultilingualActive(): boolean {
    return this.addonsAccess.isAddonActive('Multilingual') || this.addonsAccess.isAddonActive('All Addons Bundle')
  }

  openLockedModal(): void {
    const dialogRef = this.dialog.open(LimitModalComponent, {
      width: '337px',
      maxWidth: '90vw',
      panelClass: 'limit-dialog',
      backdropClass: 'blur-backdrop',
      hasBackdrop: true,
      disableClose: false,
      autoFocus: false,
      data: {
        title: 'Unlock Multilingual',
        message: 'Multilingual is locked on your plan.',
        cta: 'Unlock Multilingual'
      }
    })

    dialogRef.afterClosed().subscribe((upgrade) => {
      if (upgrade) {
        const token = localStorage.getItem('token')
        const params = new URLSearchParams()
        if (token) params.set('token', token)
        params.set('project_id', encodeURIComponent(this.builderService.selectedProject))
        window.open(`${environment.stacksURL}addons/?${params.toString()}`, '_blank')
      }
    })
  }

  /**
   * Initializes the form group for managing multilingual data.
   * Sets up the `multilingual` FormGroup with a `languages` FormArray
   * to dynamically handle the list of languages.
   */

  initForm(): void {
    this.multilingual = this.fb.group({
      languages: this.fb.array([])
    })
  }

  /**
   * Function: loadLanguages
   * Loads the list of languages and the default language from the database.
   *
   * **Steps:**
   * 1. Retrieves the project ID to construct database paths:
   *    - `languagesPath`: Path to the list of translations.
   *    - `defaultLangPath`: Path to the default language.
   *
   * **For `languagesPath`:**
   * - If languages are found:
   *   - Populates `this.language` with the existing languages as an array of objects with `title`.
   *   - Synchronizes the `selectedLanguages` array with the titles of the loaded languages for the dropdown.
   * - If no languages are found:
   *   - Logs a warning message.
   *   - Initializes `this.language` with "English" as the default language.
   *   - Sets `selectedLanguages` to `['English']` to reflect the default selection in the dropdown.
   *   - Updates the database with "English" as the default language.
   *
   * **For `defaultLangPath`:**
   * - Retrieves the default language for the project:
   *   - If a default language exists, it is set to `this.defaultLanguage`.
   *   - If no default language is found, it defaults to "English".
   *
   * **Error Handling:**
   * - Logs any errors that occur while retrieving or saving data to the database.
   *
   * **Side Effects:**
   * - Updates the UI table and the dropdown to reflect the loaded or default languages.
   */

  async loadLanguages(): Promise<void> {
    const languages = await this.translationService.getProjectLanguages()
    if (languages.length > 0) {
      this.language = languages.map((title) => ({ title }))
      this.selectedLanguages = [...languages]
    } else {
      console.warn('No languages data found. Adding "English" by default.')
      this.language = [{ title: 'English' }]
      this.selectedLanguages = ['English']
      await this.translationService.addProjectLanguage('English')
    }
    this.refreshTable()

    const config = await this.translationService.getTranslationConfig()
    this.defaultLanguage = config?.defaultLanguage ?? 'English'
    this.translateNumbers = config?.translateNumbers ?? true
  }

  /**
   * Function: onLanguagesSelected
   *
   * Handles the selection of languages from the dropdown.
   *
   * **Parameters:**
   * - `event`: The selection change event from the `mat-select` dropdown, which contains the newly selected values.
   *
   * **Steps:**
   * 1. Extracts the selected keys (`selectedKeys`) from the event's `value` property.
   * 2. Updates the `selectedLanguages` array with the newly selected keys to synchronize with the dropdown.
   * 3. Iterates through `selectedKeys` to:
   *    - Convert each key to its display name using `getLanguageName(key)`.
   *    - Check if the language is already in `this.language`.
   *    - If not, adds it to the `this.language` array as an object with a `title` property.
   * 4. Calls `refreshTable()` to update the UI and reflect the changes in the table.
   *
   * **Purpose:**
   * - Ensures the languages selected in the dropdown are dynamically added to the table.
   * - Prevents duplicate entries in the table.
   *
   * **Side Effects:**
   * - Synchronizes the dropdown (`selectedLanguages`) with the table (`this.language`).
   * - Updates the UI table to reflect the newly selected languages.
   */

  onLanguagesSelected(event: any): void {
    const selectedKey: string = event.value; // Get selected value from mat-select

    // If multilingual is NOT active, allow only ONE language
    // if (!this.isMultilingualActive()) {
      // Clear previous selection and set the new language
      this.selectedLanguages = [selectedKey];

      // Add selected language to the table if not already present
      const languageName = this.getLanguageName(selectedKey);
      if (!this.language.some((lang) => lang.title === languageName)) {
        this.language.push({ title: languageName });
      }

      this.refreshTable();
    // }
  }

  isLanguageInTable(language: string): boolean {
    return this.language.some((lang) => lang.title === this.getLanguageName(language))
  }

  get filteredLanguageCodes(): string[] {
    if (!this.dropdownSearch) return this.languageCodes
    return this.languageCodes.filter((l) => l.toLowerCase().includes(this.dropdownSearch.toLowerCase()))
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation()
    if (!this.isMultilingualActive()) {
      this.openLockedModal()
      return
    }
    this.dropdownOpen = !this.dropdownOpen
  }

  selectLanguage(lang: string, event: Event): void {
    event.stopPropagation()
    if (!this.isLanguageInTable(lang)) {
      this.onLanguagesSelected({ value: lang })
    }
    this.dropdownOpen = false
    this.dropdownSearch = ''
  }

  @HostListener('document:click')
  closeDropdown(): void {
    this.dropdownOpen = false
  }

  /**
   * Function: deleteRow
   *
   * Deletes a language from the table if it meets the conditions and updates the dropdown state.
   *
   * **Parameters:**
   * - `rowTitle`: The title of the language to be deleted.
   *
   * **Steps:**
   * 1. Checks if there is more than one language in the table:
   *    - If yes:
   *      - Prompts the user for confirmation to delete the specified language.
   *      - If confirmed:
   *        - Adds the language title to the `languagesToDelete` array for future reference (e.g., database updates).
   *        - Removes the language from the `this.language` array.
   *        - Finds the corresponding key in the `languageCodes` array using the `getLanguageName` function.
   *        - Removes the corresponding key from the `selectedLanguages` array, making the language selectable again in the dropdown.
   *        - Calls `refreshTable()` to update the UI and reflect the changes.
   *    - If no:
   *      - Displays an alert message indicating that at least one language must remain in the table.
   *
   * **Purpose:**
   * - Safely removes a language from the table after user confirmation.
   * - Updates the dropdown to make the deleted language selectable again.
   * - Ensures the table always retains at least one language.
   *
   * **Side Effects:**
   * - Updates the `languagesToDelete` array for tracking purposes.
   * - Modifies the `selectedLanguages` array to reflect the removal.
   * - Refreshes the UI table and dropdown state.
   */

  deleteRow(rowTitle: string): void {
    if (this.language.length > 1) {
      const isConfirmed = confirm(`Are you sure you want to delete "${rowTitle}"?`)
      if (isConfirmed) {
        this.languagesToDelete.push(rowTitle)
        this.language = this.language.filter((lang) => lang.title !== rowTitle)
        const languageKey = this.languageCodes.find((key) => this.getLanguageName(key) === rowTitle)
        if (languageKey) {
          this.selectedLanguages = this.selectedLanguages.filter((key) => key !== languageKey)
        }
        this.refreshTable()
      }
    } else {
      alert('At least one language must be present.')
    }
  }

  /**
   * Function: onSubmit
   *
   * Saves the current list of languages and the default language to the database.
   *
   * **Steps:**
   * 1. Sets `productProgress` to `true` to indicate the process has started.
   * 2. Retrieves the project ID and constructs the database paths:
   *    - `languagesPath`: Path for the list of translations.
   *    - `defaultLangPath`: Path for the default language.
   *
   * **For `languagesPath`:**
   * - Fetches the existing languages from the database.
   * - If existing languages are found:
   *   - Removes languages marked for deletion (`languagesToDelete`) from the database.
   *   - Clears the `languagesToDelete` array.
   *   - Adds any new languages from `this.language` that are not already in the database.
   *   - Saves the updated list of languages back to the database.
   * - If an error occurs during the fetch or save operations, logs the error and sets `productProgress` to `false`.
   *
   * **For `defaultLangPath`:**
   * - Saves the selected default language (`this.defaultLanguage`) to the database.
   * - If successful, logs a confirmation and reloads the language data.
   * - Logs an error if the save operation fails.
   *
   * **Purpose:**
   * - Ensures the languages in the table and the selected default language are saved to the database.
   * - Handles deletions, additions, and updates to maintain consistency between the UI and the database.
   *
   * **Side Effects:**
   * - Updates the database with the current state of `this.language` and `this.defaultLanguage`.
   * - Triggers a UI refresh by calling `loadLanguages()` after successful updates.
   * - Sets `productProgress` to `false` upon completion or error.
   */

  async onSubmit(): Promise<void> {
    if (!this.isMultilingualActive() && this.language.length > 1) {
      this.openLockedModal()
      return
    }
    if (this.language.length === 1) {
      this.defaultLanguage = this.language[0].title
    }
    this.productProgress = true

    await this.translationService.syncProjectLanguages(this.language.map((l) => l.title),
      this.languagesToDelete
    )
    this.languagesToDelete = []

    await this.translationService.upsertTranslationConfig({
      defaultLanguage: this.defaultLanguage,
      translateNumbers: this.translateNumbers
    })

    this.productProgress = false
    await this.loadLanguages()
  }

  /**
   * Function: selectDefaultLanguage
   *
   * Sets the specified language as the default language.
   *
   * **Parameters:**
   * - `lang` (string): The title of the language to be set as the default.
   *
   * **Steps:**
   * 1. Updates `this.defaultLanguage` with the provided language.
   *
   * **Purpose:**
   * - Allows the user to set a specific language as the default.
   *
   * **Side Effects:**
   * - Updates the `defaultLanguage` property.
   */
  selectDefaultLanguage(lang: string): void {
    this.defaultLanguage = lang
  }

  /**
   * Function: refreshTable
   *
   * Refreshes the table to reflect any changes in the `this.language` array.
   *
   * **Steps:**
   * 1. Creates a shallow copy of the `this.language` array and assigns it back to the same property.
   *
   * **Purpose:**
   * - Ensures the table UI is updated when changes are made to the `this.language` array.
   *
   * **Side Effects:**
   * - Triggers Angular's change detection to refresh the table.
   */
  refreshTable(): void {
    this.language = [...this.language]
    if (this.currentPage > this.totalPages) this.currentPage = Math.max(1, this.totalPages)
  }

  /**
   * Function: getLanguageName
   *
   * Converts a language key to a display-friendly name.
   *
   * **Parameters:**
   * - `key` (string): The key representing the language.
   *
   * **Returns:**
   * - A string where underscores (`_`) are replaced with spaces.
   *
   * **Purpose:**
   * - Converts language keys (e.g., `Chinese_simplified`) into a user-friendly format (e.g., `Chinese simplified`).
   */
  getLanguageName(key: string): string {
    return key.replace(/_/g, ' ')
  }

  /**
   * Function: getLanguageCodeKeys
   *
   * Retrieves the list of language codes.
   *
   * **Returns:**
   * - An array of strings representing all language codes.
   *
   * **Purpose:**
   * - Provides the list of language codes for use in dropdowns or other UI components.
   */
  getLanguageCodeKeys(): string[] {
    return this.languageCodes
  }
}
